// Reusable React-effect helpers for MapLibre layer lifecycle.
//
// Two patterns dominate MapView's layer toggles:
//
//   1. Pure visibility flip \u2014 N MapLibre layers share a single React
//      toggle. The effect just calls setLayoutProperty('visibility',
//      next) on each one. ~10 sites across MapView.
//
//   2. Toggle + lazy-fetch \u2014 the same visibility flip, plus the
//      first time the layer becomes visible we kick a network refresh
//      and mark the overlay as 'loaded'. ~8 sites.
//
// useLayerVisibility() and useLazyLoadedLayer() consolidate both.
// They take a getMap accessor (so they can read the current ref-held
// map without re-wiring through props) and a list of MapLibre layer
// ids whose visibility they'll flip in lockstep.
//
// Filter rebuilds and source-data mutations are NOT in here \u2014 those
// are layer-class-specific (the GFA scrubber rebuilds 19 filters; the
// NEXRAD scrubber swaps tile URLs; the sfcanalysis scrubber calls
// updateImage). They each stay in MapView so the surgical bits are
// in one place per behavior.

import { useEffect } from 'react';
import type { Map as MlMap } from 'maplibre-gl';
import {
  isOverlayLoaded,
  markOverlayLoaded,
  type OverlayId,
} from './overlayRuntime';

/**
 * Flip visibility on one or more MapLibre layers in lockstep when
 * `visible` changes. Skips layers that haven't been added to the
 * map yet (typical during the brief window between MapView mount
 * and addOverlays() running).
 *
 * Pass `styleEpoch` so the effect re-runs after every basemap rebuild;
 * otherwise the freshly-recreated MapLibre layers stay at their
 * default visibility (typically 'none') even when the React toggle is
 * still ON. Bug class fixed 2026-05-13.
 */
export function useLayerVisibility(
  getMap: () => MlMap | null,
  layerIds: ReadonlyArray<string>,
  visible: boolean,
  styleEpoch = 0,
): void {
  // Sort + join so we can derive a stable dep key without forcing
  // callers to memoize their array prop.
  const layerKey = [...layerIds].sort().join('|');
  useEffect(() => {
    const map = getMap();
    if (!map) return;
    const v = visible ? 'visible' : 'none';
    for (const id of layerIds) {
      if (!map.getLayer(id)) continue;
      map.setLayoutProperty(id, 'visibility', v);
    }
    // layerKey is included so a swap to a different set of layer ids
    // re-runs the effect; layerIds itself isn't in the deps because
    // it's likely a fresh array each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMap, layerKey, visible, styleEpoch]);
}

/**
 * Combine layer-visibility flipping with first-show lazy fetch. On
 * the first render where `visible` is true and the overlay is not
 * yet marked loaded, calls `refresh(map)` and flips the load flag.
 * Subsequent toggles only flip visibility (no extra network).
 *
 * Use overlayRuntime's OverlayId for the load-flag key.
 */
export function useLazyLoadedLayer(
  getMap: () => MlMap | null,
  layerIds: ReadonlyArray<string>,
  visible: boolean,
  overlayId: OverlayId,
  refresh: (map: MlMap) => unknown,
  styleEpoch = 0,
): void {
  const layerKey = [...layerIds].sort().join('|');
  useEffect(() => {
    const map = getMap();
    if (!map) return;
    const v = visible ? 'visible' : 'none';
    for (const id of layerIds) {
      if (!map.getLayer(id)) continue;
      map.setLayoutProperty(id, 'visibility', v);
    }
    if (visible && !isOverlayLoaded(overlayId)) {
      markOverlayLoaded(overlayId, true);
      void refresh(map);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMap, layerKey, visible, overlayId, styleEpoch]);
}
